/**
 * src/lib/onboard-default-search-terms.ts
 *
 * Canonical 151-term search-keyword set used as the Stage-3 default
 * for new scrapers. Mirrors `CEX_SEARCH_TERMS` / `AMAZON_SEARCH_TERMS`
 * in `backend/scrapers/cloudflare_workers/.env`, which is the
 * authoritative production list for CeX, Amazon, eBay, and the rest
 * of the multi-site scraper fleet.
 *
 * Why hard-code instead of fetch-from-server?
 *   - This list is display-default only; the operator edits before
 *     submitting Stage 3 anyway.
 *   - The wire payload is the operator's edited list, not this
 *     constant — so going stale here doesn't change actual prod
 *     behavior, just the prefilled UI.
 *   - Avoids an extra round-trip to render the form.
 *
 * Keep in sync: when the .env list changes, regenerate this file.
 * The `wc -l` count below should match the .env's term count.
 */

/** 151-term canonical search-keyword list (matches CEX_SEARCH_TERMS in
 *  backend/scrapers/cloudflare_workers/.env). */
export const DEFAULT_SEARCH_TERMS_CSV = [
  // phones
  'iphone 15 pro max', 'iphone 15 pro', 'iphone 15',
  'iphone 14 pro max', 'iphone 14 pro', 'iphone 14',
  'iphone 13 pro max', 'iphone 13 pro', 'iphone 13',
  'iphone 12 pro max', 'iphone 12 pro', 'iphone 12',
  'iphone 11 pro max', 'iphone 11 pro', 'iphone 11',
  'iphone xr', 'iphone xs max', 'iphone xs', 'iphone x',
  'iphone 8 plus', 'iphone 8', 'iphone 7',
  'samsung galaxy s24 ultra', 'samsung galaxy s24+', 'samsung galaxy s24',
  'samsung galaxy s23 ultra', 'samsung galaxy s23+', 'samsung galaxy s23',
  'samsung galaxy s22 ultra', 'samsung galaxy s22+', 'samsung galaxy s22',
  'samsung galaxy s21',
  'samsung galaxy a54', 'samsung galaxy a53', 'samsung galaxy a52',
  'samsung galaxy a34', 'samsung galaxy a33', 'samsung galaxy note 20',
  'pixel 8 pro', 'pixel 8', 'pixel 7 pro', 'pixel 7', 'pixel 7a',
  'pixel 6 pro', 'pixel 6', 'pixel 6a',
  'oneplus 12', 'oneplus 11', 'oneplus 10 pro', 'oneplus 9 pro', 'oneplus nord',
  'huawei p50', 'huawei p40 pro',
  // laptops
  'macbook pro m3', 'macbook pro m2', 'macbook pro m1',
  'macbook air m3', 'macbook air m2', 'macbook air m1',
  'macbook pro 16', 'macbook pro 14', 'macbook pro 13',
  'dell xps 15', 'dell xps 13', 'dell inspiron',
  'hp spectre x360', 'hp envy',
  'lenovo thinkpad x1', 'lenovo thinkpad t14', 'lenovo ideapad',
  'asus zenbook', 'asus rog',
  'surface laptop', 'surface pro',
  // consoles
  'ps5', 'ps4 pro', 'ps4',
  'xbox series x', 'xbox series s', 'xbox one x',
  'nintendo switch oled', 'nintendo switch', 'nintendo switch lite',
  'steam deck',
  'ps5 controller', 'ps4 controller', 'xbox controller',
  // tablets
  'ipad pro 12.9', 'ipad pro 11', 'ipad air', 'ipad mini', 'ipad 10th', 'ipad 9th',
  'samsung galaxy tab s9', 'samsung galaxy tab s8', 'samsung galaxy tab a8',
  // audio
  'airpods pro 2nd', 'airpods pro', 'airpods 3rd', 'airpods 2nd',
  'sony wh-1000xm5', 'sony wh-1000xm4', 'sony wf-1000xm5', 'sony wf-1000xm4',
  'bose quietcomfort 45', 'bose quietcomfort 35',
  // wearables
  'apple watch ultra', 'apple watch series 9', 'apple watch series 8', 'apple watch series 7',
  'samsung galaxy watch 6', 'samsung galaxy watch 5',
  'fitbit sense', 'garmin fenix',
  // cameras
  'canon eos r6', 'canon eos r5', 'canon eos r50',
  'sony a7 iv', 'sony a7 iii',
  'nikon z6', 'nikon z7',
  'fujifilm x-t5', 'fujifilm x-t4',
  'gopro hero 12', 'gopro hero 11',
  // GPUs / monitors
  'rtx 4090', 'rtx 4080', 'rtx 4070', 'rtx 4060',
  'rtx 3090', 'rtx 3080', 'rtx 3070', 'rtx 3060',
  'radeon rx 7900', 'radeon rx 6800',
  'odyssey g9',
  // games
  'elden ring', 'god of war', 'spider-man', 'horizon forbidden west',
  'last of us', 'zelda tears', 'mario kart 8', 'pokemon scarlet',
  'fifa 24', 'fifa 23', 'call of duty', 'red dead redemption',
  'grand theft auto', 'minecraft', 'fortnite',
].join(', ');

/** Number of terms in DEFAULT_SEARCH_TERMS_CSV (split on comma). */
export const DEFAULT_SEARCH_TERMS_COUNT = DEFAULT_SEARCH_TERMS_CSV.split(',').length;
